import { useEffect, useMemo, useRef } from 'react';

type ClickHandlerProps = {
  onSingleClick?: () => void;
  onDoubleClick?: () => void;
  onTripleClick?: () => void;
  delay?: number; // задержка между кликами в ms
};

export const useMultiClick = ({
  onSingleClick,
  onDoubleClick,
  onTripleClick,
  delay = 300,
}: ClickHandlerProps) => {
  const clickCount = useRef(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Динамически вычисляем максимальное количество кликов на основе переданных обработчиков
  const maxClicks = useMemo(() => {
    if (onTripleClick) return 3;
    if (onDoubleClick) return 2;
    if (onSingleClick) return 1;
    return 0;
  }, [onSingleClick, onDoubleClick, onTripleClick]);

  const handleClick = () => {
    if (maxClicks === 0) return;

    clickCount.current += 1;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (clickCount.current === maxClicks) {
      // Максимальное количество кликов достигнуто - выполняем сразу
      switch (clickCount.current) {
        case 1:
          onSingleClick?.();
          break;
        case 2:
          onDoubleClick?.();
          break;
        case 3:
          onTripleClick?.();
          break;
      }
      clickCount.current = 0;
    } else {
      // Ждем следующие клики в течение задержки
      timeoutRef.current = setTimeout(() => {
        if (clickCount.current > 0) {
          switch (clickCount.current) {
            case 1:
              onSingleClick?.();
              break;
            case 2:
              onDoubleClick?.();
              break;
            case 3:
              onTripleClick?.();
              break;
          }
        }
        clickCount.current = 0;
      }, delay);
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return handleClick;
};
